import { injectable } from 'inversify';
import Debug from 'debug';
import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { createUpserter, mergeRightToLeft } from '@/app/shared/utils'
import { SectionState, SectionStatus, defaultSectionState, statusAsNumber } from './section-state';
import { SectionStateQuery } from './section-state-query';

export const SectionStateStoreModuleSymbol = Symbol.for('SectionStateStoreModule');

const debug = Debug('section-state-store-module');

const upserter = createUpserter((id) => defaultSectionState(id));

interface AddDurationPayload { id: string; durationSeconds: number; }
interface ChangeStatusPayload { id: string; status: SectionStatus; }

@injectable()
@Module({
  namespaced: true,
  name: 'sectionState',
  store,
  dynamic: true
})
export class SectionStateStoreModule extends VuexModule implements SectionStateQuery {

  private remotePhase: 'none' | 'loading' | 'loaded';
  private remote: SectionState[] = [];
  private local: SectionState[] = [];

  @Mutation
  public startLoading() {
    this.remotePhase = 'loading';
  }

  @Mutation
  public finishLoading(sections: SectionState[]) {
    this.remotePhase = 'loaded';
    this.remote = sections;
  }

  @Mutation
  public addDuration({ id, durationSeconds }: AddDurationPayload) {
    this.local = upserter(this.local, id, (data) => {
      return {
        ...data,
        durationSeconds: data.durationSeconds + durationSeconds
      };
    });

    debug('new local state', this.local);
  }


  @Mutation
  public changeStatus({ id, status }: ChangeStatusPayload) {

    const updated = upserter(this.local, id, (data) => {

      if (data.status === status) {
        return false;
      }

      return {
        ...data,
        status
      };
    });

    this.local = updated;
    debug('new local state', this.local);
  }

  get states() {

    const merged = mergeRightToLeft(this.local, this.remote, merge)
    return merged;
  }

  get courseTimeSeconds() {
    return this.states.reduce((a, b) => a + b.durationSeconds, 0);
  }
}

function merge(local: SectionState, remote: SectionState): SectionState {

  const status = statusAsNumber(remote.status) > statusAsNumber(local.status) ?
        remote.status : local.status
  
  return {
    id: local.id,
    status: status,
    durationSeconds: local.durationSeconds + remote.durationSeconds
  }
}

export default getModule(SectionStateStoreModule);
